import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link2, Check } from "lucide-react";

/**
 * CopyLink – fixed top-right pill button that copies the current URL and
 * shows an inline "Link kopiert" confirmation for ~2 seconds.
 * Uses only browser APIs, no external toast dependency.
 */
export default function CopyLink() {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const url =
      typeof window !== "undefined" ? window.location.href : "";
    let ok = false;
    // Preferred: async Clipboard API
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        ok = true;
      }
    } catch {
      /* fall through to fallback */
    }
    // Fallback: legacy execCommand (works in restricted contexts)
    if (!ok) {
      try {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.top = "-1000px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        ok = true;
      } catch {
        /* silent */
      }
    }
    // Always show visual feedback so user knows the button responded
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed right-4 top-4 z-30 sm:right-6 sm:top-6">
      <motion.button
        type="button"
        onClick={handleCopy}
        data-testid="copy-link-btn"
        aria-label="Link zu dieser Seite kopieren"
        whileTap={{ scale: 0.96 }}
        className="group inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.03] px-3.5 py-2 text-xs font-medium text-zinc-300 backdrop-blur-xl transition-all duration-300 hover:border-white/20 hover:bg-white/[0.06] hover:text-white"
      >
        <AnimatePresence mode="wait" initial={false}>
          {copied ? (
            <motion.span
              key="done"
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 4 }}
              transition={{ duration: 0.25 }}
              className="flex items-center gap-2"
            >
              <Check
                size={13}
                strokeWidth={2.4}
                className="text-[#CCFF00]"
              />
              <span data-testid="copy-link-toast">Link kopiert</span>
            </motion.span>
          ) : (
            <motion.span
              key="idle"
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 4 }}
              transition={{ duration: 0.25 }}
              className="flex items-center gap-2"
            >
              <Link2
                size={13}
                strokeWidth={2}
                className="transition-transform duration-300 group-hover:-rotate-12"
              />
              <span>Link kopieren</span>
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>
    </div>
  );
}
